import { platformService } from '../../shared/services/platform.service';
import { FILTERS } from '../../shared/utils/constants';
import { FormQuestion, TemplateFormDefinition, isFormColumnKey, singleTemplateId, templateFormQuestions } from './formColumns';

const questionsByTemplate = new Map<number, Promise<FormQuestion[]>>();

/**
 * A template's form questions, fetched once per template. TicketTemplate is scoped on the server, so a template the
 * agent may not use comes back empty and its questions never reach the list - which is what keeps a scoped-out
 * template's answers off screen. A failed fetch is not remembered, so the next view load asks again.
 */
export const loadTemplateQuestions = (templateId: number): Promise<FormQuestion[]> => {
  if (!questionsByTemplate.has(templateId)) {
    const loading = new platformService()
      // Form takes no $select inside an expand here - OData refuses it ("$expand option is not allowed").
      .getTicketTemplates(`?$filter=Id eq ${templateId}&$select=Id&$expand=TemplateForms($expand=Form)`)
      .then(response => {
        const template = response.data?.value?.[0] as { TemplateForms?: TemplateFormDefinition[] } | undefined;
        const questions = templateFormQuestions(template?.TemplateForms ?? []);
        questions.forEach(question => formQuestionByKey.set(question.key, question.formQuestion));
        return questions;
      })
      .catch(() => {
        questionsByTemplate.delete(templateId);
        return [] as FormQuestion[];
      });
    questionsByTemplate.set(templateId, loading);
  }
  return questionsByTemplate.get(templateId);
};

/** Which question a form column key stands for, as saved in ViewColumn.FormQuestion. */
const formQuestionByKey = new Map<number, string>();

export const rememberFormQuestion = (key: number, formQuestion: string) => {
  if (formQuestion) formQuestionByKey.set(Number(key), formQuestion);
};

export const formQuestionOfKey = (key: number) => formQuestionByKey.get(Number(key));

/**
 * Every question of the one template a view's filter names, whatever columns the view shows - what the CSV export writes,
 * as it writes every custom property (Shaun, 2026-10-15). Empty without exactly one template, or for one the agent may
 * not use.
 */
export const viewTemplateQuestions = async (view: { ViewFilters?: ViewFilter[] } | null | undefined): Promise<FormQuestion[]> => {
  const templateFilter = view?.ViewFilters?.find(filter => filter.FilterKey === FILTERS.TEMPLATE.id);
  const templateId = singleTemplateId(templateFilter?.FilterValues?.split(',') ?? []);
  return templateId ? loadTemplateQuestions(templateId) : [];
};

/**
 * The form question columns a saved view shows: its saved questions that its one template still asks. Empty when the
 * view's Template filter does not name exactly one template, or names one the agent may not use.
 */
export const viewFormQuestions = async (view: { ViewFilters?: ViewFilter[]; ViewColumns?: ViewColumn[] } | null | undefined): Promise<FormQuestion[]> => {
  if (!view) return [];
  // A view saved with no columns chosen shows every column - its template's questions too (Shaun, 2026-10-15).
  const showsEverything = (view.ViewColumns ?? []).length === 0;
  const saved = (view.ViewColumns ?? []).filter(column => column.FormQuestion && isFormColumnKey(column.ColumnKey)).map(column => column.FormQuestion);
  if (!showsEverything && saved.length === 0) return [];
  const templateFilter = view.ViewFilters?.find(filter => filter.FilterKey === FILTERS.TEMPLATE.id);
  const templateId = singleTemplateId(templateFilter?.FilterValues?.split(',') ?? []);
  if (!templateId) return [];
  const questions = await loadTemplateQuestions(templateId);
  return showsEverything ? questions : questions.filter(question => saved.includes(question.formQuestion));
};
